import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, User, Heart, ShoppingBag, Menu, Instagram, X } from "lucide-react";
import { Logo } from "./Logo";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useStore } from "@/context/StoreContext";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const STATIC_NAV = [{ label: "Collections", to: "/collections" }];
const buildNav = (categories) => {
  const cats = (categories || []).slice().sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
  const main = cats.filter((c) => !["sale", "best-sellers", "new-arrivals"].includes(c.slug)).slice(0, 3).map((c) => ({ label: c.name, to: `/shop/${c.slug}` }));
  const newArr = cats.find((c) => c.slug === "new-arrivals");
  const best = cats.find((c) => c.slug === "best-sellers");
  const sale = cats.find((c) => c.slug === "sale");
  return [
    ...(newArr ? [{ label: "New Arrivals", to: "/shop/new-arrivals" }] : []),
    ...main, ...STATIC_NAV,
    ...(best ? [{ label: "Best Sellers", to: "/shop/best-sellers" }] : []),
    ...(sale ? [{ label: "Sale", to: "/shop/sale" }] : []),
  ];
};

export default function Header() {
  const { count, setOpen } = useCart();
  const { user, wishlist } = useAuth();
  const { content, categories } = useStore();
  const NAV = buildNav(categories);
  const split = Math.ceil(NAV.length / 2);
  const nav = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const ann = content?.announcement;

  const doSearch = (e) => {
    e.preventDefault();
    if (q.trim()) { nav(`/shop?search=${encodeURIComponent(q.trim())}`); setSearchOpen(false); setMenuOpen(false); }
  };

  return (
    <>
      {ann?.enabled && (
        <div data-testid="announcement-bar" className="bg-sharoa-rose text-white text-[11px] sm:text-xs tracking-[0.18em] uppercase text-center py-2 px-4 font-medium">
          {ann.text}
        </div>
      )}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-white/90 border-b border-sharoa-line">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="flex items-center justify-between h-20 sm:h-24">
            {/* Mobile menu */}
            <div className="flex items-center gap-2 lg:hidden">
              <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
                <SheetTrigger asChild>
                  <button data-testid="mobile-menu-btn" className="p-2 -ml-2 text-sharoa-charcoal"><Menu size={22} /></button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[85vw] sm:w-96 bg-white p-0">
                  <div className="p-6 border-b border-sharoa-line"><Logo className="h-14" /></div>
                  <nav className="flex flex-col p-2">
                    {NAV.map((n) => (
                      <Link key={n.label} to={n.to} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g,"-")}`}
                        onClick={() => setMenuOpen(false)}
                        className="px-4 py-3.5 font-serif text-lg text-sharoa-charcoal border-b border-sharoa-line/60">
                        {n.label}
                      </Link>
                    ))}
                  </nav>
                  <div className="p-6 flex items-center gap-4">
                    <a href={`https://instagram.com/sharoaofficial`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-sharoa-muted">
                      <Instagram size={18} /> @sharoaofficial
                    </a>
                  </div>
                </SheetContent>
              </Sheet>
            </div>

            {/* Desktop left nav */}
            <nav className="hidden lg:flex items-center gap-7 flex-1">
              {NAV.slice(0, split).map((n) => (
                <Link key={n.label} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s/g,"-")}`}
                  className="text-[12px] uppercase tracking-[0.14em] text-sharoa-charcoal sh-underline">{n.label}</Link>
              ))}
            </nav>

            <div className="flex-1 lg:flex-none flex justify-center"><Logo className="h-12 sm:h-16 lg:h-[72px]" /></div>

            <div className="flex items-center gap-3 sm:gap-5 lg:flex-1 lg:justify-end">
              <nav className="hidden lg:flex items-center gap-7 mr-2">
                {NAV.slice(split).map((n) => (
                  <Link key={n.label} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s/g,"-")}`}
                    className="text-[12px] uppercase tracking-[0.14em] text-sharoa-charcoal sh-underline">{n.label}</Link>
                ))}
              </nav>
              <button data-testid="nav-search-btn" onClick={() => setSearchOpen((v) => !v)} className="text-sharoa-charcoal hover:text-sharoa-rose transition-colors"><Search size={19} /></button>
              <Link to={user ? "/account" : "/login"} data-testid="nav-account-btn" className="hidden sm:block text-sharoa-charcoal hover:text-sharoa-rose transition-colors"><User size={19} /></Link>
              <Link to="/account?tab=wishlist" data-testid="nav-wishlist-btn" className="relative text-sharoa-charcoal hover:text-sharoa-rose transition-colors">
                <Heart size={19} />
                {wishlist?.length > 0 && <span className="absolute -top-2 -right-2 bg-sharoa-rose text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center">{wishlist.length}</span>}
              </Link>
              <button data-testid="nav-cart-btn" onClick={() => setOpen(true)} className="relative text-sharoa-charcoal hover:text-sharoa-rose transition-colors">
                <ShoppingBag size={19} />
                {count > 0 && <span className="absolute -top-2 -right-2 bg-sharoa-rose text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center">{count}</span>}
              </button>
            </div>
          </div>
          {searchOpen && (
            <form onSubmit={doSearch} className="pb-4 flex items-center gap-2 sh-fade-up">
              <input data-testid="search-input" autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search for tops, knits, crops…"
                className="flex-1 border-b border-sharoa-rose/50 py-2 outline-none bg-transparent text-sm placeholder:text-sharoa-muted" />
              <button type="button" onClick={() => setSearchOpen(false)}><X size={18} className="text-sharoa-muted" /></button>
            </form>
          )}
        </div>
      </header>
    </>
  );
}
